/**
 * O acesso do frontend ao monitoramento.
 *
 * 🔴 Nenhuma conta acontece aqui. Os quatro estados, a disponibilidade e a
 * separação entre "fora do ar" e "sem informação" são decididos no SERVIDOR e
 * chegam prontos. Recalcular qualquer um deles na tela criaria um segundo
 * motor da mesma regra — e o dia em que os dois discordassem, ninguém saberia
 * qual está certo.
 *
 * ⚠️ Zero `localStorage`: o que a pessoa escolhe na tela (cliente, busca,
 * estados) vive em React state e na query string, como manda o projeto.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type EstadoMonitoramento = 'no_ar' | 'fora_do_ar' | 'sem_informacao' | 'silenciado';

/** 🔴 Os quatro, separados. Não existe — e não deve passar a existir — um "com problema". */
export interface ContagemPorEstado {
  noAr: number;
  foraDoAr: number;
  semInformacao: number;
  silenciados: number;
}

export interface EquipamentoAoVivo {
  id: string;
  nome: string;
  endereco: string;
  tipo: string | null;
  metodo: 'ping' | 'tcp';
  porta: number | null;
  metodoMotivo: string | null;
  senhaEquipId: string | null;
  estado: EstadoMonitoramento;
  rotulo: string;
  desdeIso: string | null;
  ultimoTesteIso: string | null;
  latenciaMs: number | null;
  silenciadoEmIso: string | null;
  silenciadoMotivo: string | null;
}

export interface ClienteAoVivo {
  clienteId: string;
  razaoSocial: string;
  /** AMBIENTE DE ENSAIO (mig 561): a máquina da IGM onde toda versão nova
   *  estreia. Marcado na tela; fora de indicadores, ranking e totais. */
  ensaio?: boolean;
  contagem: ContagemPorEstado;
  coletorMudo: boolean;
  pausado: boolean;
  pausadoMotivo: string | null;
  ultimoContatoIso: string | null;
  versao: string | null;
  /**
   * 🔴 A versão deixou de ser texto decorativo (03/09/2026).
   *
   * `versaoAtrasada` compara com a mais recente PUBLICADA. Um coletor pode
   * estar atrasado de propósito — ninguém liberou ainda para ele —, e a tela
   * mostra isso sem chamar de problema.
   */
  versaoAtrasada?: boolean;
  atualizacaoStatus?: string | null;
  atualizacaoDetalhe?: string | null;
  /**
   * 🔴 Recebeu ordem de atualizar e não deu mais notícia — nem sucesso, nem
   * falha. É o retrato do antivírus que deixa escrever o binário novo e
   * bloqueia a execução dele: o processo morre e não sobra ninguém para avisar.
   * ⚠️ Diferente de "sem contato" comum, onde a causa é desconhecida.
   */
  sumiuAposOrdem?: boolean;
  /** A versao liberada para ESTE cliente, se alguem liberou. */
  versaoLiberada?: string | null;
  /**
   * 🔴 A volta ARMADA (mig 535): a versao nova instalou e o coletor emudeceu.
   * O servidor volta a anunciar a versao anterior — um downgrade e uma
   * atualizacao para tras, e por isso cabe no coletor que ja esta em campo.
   */
  voltaArmadaPara?: string | null;
  observandoAteIso?: string | null;
  equipamentos: EquipamentoAoVivo[];
}

export interface RespostaAoVivo {
  clientes: ClienteAoVivo[];
  totais: ContagemPorEstado & { clientesSemInformacao: number };
  config: { intervalo_seg: number; falhas_para_queda: number; sem_noticia_seg: number };
}

export interface ValorHerdado<T> {
  valor: T;
  origem: 'global' | 'cliente' | 'equipamento';
}

export interface ConfigEfetiva {
  intervaloSeg: ValorHerdado<number>;
  falhasParaQueda: ValorHerdado<number>;
  semNoticiaSeg: ValorHerdado<number>;
}

export interface FichaDoCliente extends ClienteAoVivo {
  coletor: {
    token_prefixo: string | null;
    token_gerado_em_iso: string | null;
    /** A-KEY-QUE-SOME (10/09): 1 = a key tem cópia cifrada (Mostrar/Copiar). A cifra nunca desce. */
    key_exibivel?: number | boolean;
    /**
     * Como o coletor subiu: 'servico' | 'tarefa' | 'manual'.
     * ⚠️ NULL = coletor antigo, que não manda o campo (mig 528).
     */
    iniciado_por?: string | null;
    instalado_em_iso: string | null;
    varredura_pedida_em_iso: string | null;
    varredura_entregue_iso: string | null;
    varreduraPendente: boolean;
    /** A conclusão da varredura remota (mig 565) — o que encerra a espera. */
    varredura_concluida_iso?: string | null;
    varredura_resumo?: string | null;
    /** Em quantos segundos o coletor deve fazer o próximo contato. */
    proximoContatoEstimadoSeg?: number | null;
    /** O carimbo do último lote — a régua de "fala" ou "mudo" da tela. */
    ultimo_contato_iso?: string | null;
    /** Onde o coletor vive. `null` = ninguém disse ainda. */
    onde_roda: 'servidor_do_cliente' | 'dispositivo_dedicado' | null;
    onde_roda_detalhe: string | null;
    /** Frente 3: o cliente saiu do monitoramento. O histórico fica. */
    encerrado_em_iso: string | null;
    encerrado_motivo: string | null;
  };
  config: ConfigEfetiva;
  /** OSCILACAO (557): tipos de medição com a exceção DESTE cliente. */
  tiposMedicao?: TipoMedicaoDoCliente[];
}

export interface TipoMedicaoDoCliente {
  slug: string;
  nome: string;
  descricao: string;
  essencial: boolean;
  ligadoGlobal: boolean;
  /** null = herda o global. */
  override: boolean | null;
  efetivo: boolean;
}

export interface LinhaIndicador {
  equipamentoId: string;
  nome: string;
  endereco: string;
  clienteId: string;
  /** #566: o nome na tela (fantasia, senão razão social). Opcional: servidor antigo não o manda. */
  clienteNome?: string;
  razaoSocial: string;
  quedas: number;
  /** `null` = não houve informação no período. Nunca confundir com 0%. */
  disponibilidadePct: number | null;
  medidoSobrePct: number;
  segNoAr: number;
  segFora: number;
  segSemInfo: number;
  segSilenciado: number;
}

export interface EpisodioNaTela {
  id: string;
  equipamentoId: string | null;
  equipamentoNome: string | null;
  tipo: string;
  inicioIso: string;
  fimIso: string | null;
  comoTerminou: string | null;
  duracaoSeg: number | null;
}

const RAIZ = '/api/v1/monitoramento';
const chave = (...partes: unknown[]) => ['v1', 'monitoramento', ...partes] as const;

/**
 * O estado agora.
 *
 * ⚠️ Recarrega sozinha a cada 30s. "Ao vivo" é o mais recente que CHEGOU, não
 * o instante presente — por isso a tela mostra a idade do dado em vez de
 * fingir um tempo real que não existe.
 */
export function useAoVivo(ativo = true) {
  return useQuery({
    queryKey: chave('ao-vivo'),
    queryFn: () => api.get<RespostaAoVivo>(`${RAIZ}/ao-vivo`),
    enabled: hasToken() && ativo,
    refetchInterval: 30_000,
    retry: false,
  });
}

export function useIndicadores(de: string, ate: string) {
  return useQuery({
    queryKey: chave('indicadores', de, ate),
    queryFn: () =>
      api.get<{ de: string; ate: string; linhas: LinhaIndicador[] }>(
        `${RAIZ}/indicadores?de=${encodeURIComponent(de)}&ate=${encodeURIComponent(ate)}`,
      ),
    enabled: hasToken() && Boolean(de && ate),
    retry: false,
  });
}

/**
 * OSCILACAO (557): os SINAIS das três detecções, lidos da janela por hora.
 * A janela e os limiares são configuráveis (nunca número fixo no código) e
 * voltam junto para a tela dizer com que régua está julgando.
 */
export interface SinalDoEquipamento {
  equipamentoId: string;
  nome: string;
  clienteId: string;
  /** #566: o nome na tela (fantasia, senão razão social). Opcional: servidor antigo não o manda. */
  clienteNome?: string;
  razaoSocial: string;
  quedasNaJanela: number;
  transicoesNaJanela: number;
  oscilando: boolean;
  latenciaRecenteMs: number | null;
  latenciaHistoricaMs: number | null;
  latenciaSubindo: boolean;
  perdaPct: number | null;
  perdaDePacote: boolean;
  testesNaJanela: number;
}

export interface ConfigDeteccoes {
  janelaHoras: number;
  quedasMin: number;
  latenciaFator: number;
  perdaPctMin: number;
}

export function useSinais(ativa = true) {
  return useQuery({
    queryKey: chave('sinais'),
    queryFn: () =>
      api.get<{ config: ConfigDeteccoes; sinais: SinalDoEquipamento[] }>(`${RAIZ}/sinais`),
    enabled: hasToken() && ativa,
    refetchInterval: 60_000,
  });
}

export function useMonitoramentoDoCliente(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: chave('cliente', clienteId ?? ''),
    queryFn: () => api.get<FichaDoCliente>(`${RAIZ}/clientes/${encodeURIComponent(clienteId!)}`),
    enabled: hasToken() && !!clienteId,
    refetchInterval: 30_000,
    // 404 = cliente sem coletor. É uma resposta, não uma falha para retentar.
    retry: false,
  });
}

/**
 * A TRILHA — quem fez o quê no monitoramento deste cliente.
 *
 * 🔴 Não confundir com o Histórico (`useHistoricoDoCliente`, logo abaixo).
 * Histórico = o que aconteceu com o EQUIPAMENTO. Trilha = quem AGIU.
 *
 * ⚠️ Lê a `audit_log` que já existia — a mesma de Acessos e senhas. Nada foi
 * criado para isto.
 */
export interface LinhaDaTrilha {
  id: string;
  acao: string;
  rotulo: string;
  alvo: string | null;
  motivo: string | null;
  prefixo: string | null;
  versao: string | null;
  /** `null` quando a ação veio pela KEY do coletor, não por uma pessoa. */
  autor: string | null;
  quandoIso: string;
}

export function useAuditoriaDoCliente(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: chave('auditoria', clienteId ?? ''),
    queryFn: () =>
      api.get<{ itens: LinhaDaTrilha[] }>(
        `${RAIZ}/clientes/${encodeURIComponent(clienteId!)}/auditoria`,
      ),
    enabled: hasToken() && !!clienteId,
    retry: false,
  });
}

export function useHistoricoDoCliente(
  clienteId: string | null | undefined,
  equipamentoId?: string | null,
  // Adendo 11/09: filtro por período + o TOTAL (a tela diz "mostrando X de Y").
  periodo?: { desdeIso?: string | null; ateIso?: string | null },
) {
  const q = new URLSearchParams();
  if (equipamentoId) q.set('equipamentoId', equipamentoId);
  if (periodo?.desdeIso) q.set('desdeIso', periodo.desdeIso);
  if (periodo?.ateIso) q.set('ateIso', periodo.ateIso);
  const sufixo = q.toString() ? `?${q.toString()}` : '';
  return useQuery({
    queryKey: chave('historico', clienteId ?? '', equipamentoId ?? '', periodo?.desdeIso ?? '', periodo?.ateIso ?? ''),
    queryFn: () =>
      api.get<{ episodios: EpisodioNaTela[]; total?: number; limite?: number }>(
        `${RAIZ}/clientes/${encodeURIComponent(clienteId!)}/historico${sufixo}`,
      ),
    enabled: hasToken() && !!clienteId,
    retry: false,
  });
}

/** O que a aba mostra do cofre. Vem vazio para quem não pode ver o cofre. */
export function useCofreDoMonitoramento(clienteId: string | null | undefined) {
  return useQuery({
    queryKey: chave('cofre', clienteId ?? ''),
    queryFn: () =>
      api.get<{
        podeVerCofre: boolean;
        acessosRemotos: Array<{ id: string; nome_pc: string; ferramenta: string; id_acesso: string | null }>;
        senhasDeEquipamento: Array<{ id: string; equipamento: string; is_padrao: number }>;
      }>(`${RAIZ}/clientes/${encodeURIComponent(clienteId!)}/cofre`),
    enabled: hasToken() && !!clienteId,
    retry: false,
  });
}

/**
 * A sugestão de vínculo com o cofre.
 *
 * 🔴 Sugestão, nunca vínculo automático: nome parecido não é prova de que é o
 * mesmo aparelho, e amarrar errado é pior do que não amarrar.
 */
export function useSenhasParecidas(clienteId: string | null | undefined, nome: string) {
  return useQuery({
    queryKey: chave('senhas-parecidas', clienteId ?? '', nome),
    queryFn: () =>
      api.get<{
        podeVerCofre: boolean;
        candidatos: Array<{ id: string; equipamento: string; porque: string }>;
      }>(`${RAIZ}/clientes/${encodeURIComponent(clienteId!)}/senhas-parecidas?nome=${encodeURIComponent(nome)}`),
    enabled: hasToken() && !!clienteId && nome.trim().length >= 3,
    retry: false,
  });
}

export interface EquipamentoInput {
  nome: string;
  endereco: string;
  tipo?: string | null;
  porta?: number | null;
  intervaloSeg?: number | null;
  falhasParaQueda?: number | null;
  senhaEquipId?: string | null;
  /**
   * 🔴 A credencial (mig 565): no CADASTRO a resposta é obrigatória — senha
   * ou o marcador "sem senha". A senha vai direto ao cofre, cifrada; nunca
   * volta em resposta nenhuma.
   */
  usuario?: string | null;
  senha?: string | null;
  semSenha?: boolean;
}

/** As ações. Toda uma invalida o que a tela mostra — sem recarregar a página. */
export function useMonitoramentoActions(clienteId: string) {
  const qc = useQueryClient();
  const raiz = `${RAIZ}/clientes/${encodeURIComponent(clienteId)}`;
  const atualizar = () => {
    void qc.invalidateQueries({ queryKey: chave('cliente', clienteId) });
    void qc.invalidateQueries({ queryKey: chave('ao-vivo') });
  };

  return {
    instalarColetor: useMutation({
      mutationFn: () => api.post<{ token: string; prefixo: string; aviso: string }>(`${raiz}/coletor`),
      onSuccess: atualizar,
    }),
    salvarTipoMedicao: useMutation({
      // `ligado: null` remove a exceção — o cliente volta a herdar o global.
      mutationFn: (p: { slug: string; ligado: boolean | null }) =>
        api.put(`${raiz}/tipos-medicao/${encodeURIComponent(p.slug)}`, { ligado: p.ligado }),
      onSuccess: atualizar,
    }),
    salvarConfig: useMutation({
      mutationFn: (c: Partial<Record<'intervaloSeg' | 'falhasParaQueda' | 'semNoticiaSeg', number | null>>) =>
        api.patch<{ config: ConfigEfetiva }>(`${raiz}/config`, c),
      onSuccess: atualizar,
    }),
    pausar: useMutation({
      mutationFn: (motivo: string) => api.post(`${raiz}/pausar`, { motivo }),
      onSuccess: atualizar,
    }),
    // AMBIENTE DE ENSAIO (mig 561) — admin marca/desmarca; muda a régua da
    // versão provada e tira o cliente de indicadores e ranking.
    ambienteEnsaio: useMutation({
      mutationFn: (ensaio: boolean) => api.patch(`${raiz}/ambiente-ensaio`, { ensaio }),
      onSuccess: atualizar,
    }),
    /**
     * 🔴 ENCERRAR é diferente de PAUSAR. Pausar é "há técnico no local, volto
     * já". Encerrar é "este cliente não é mais monitorado" — o histórico fica
     * inteiro, e o coletor instalado se desliga sozinho.
     */
    encerrar: useMutation({
      mutationFn: (motivo: string) => api.post(`${raiz}/encerrar`, { motivo }),
      onSuccess: atualizar,
    }),
    /**
     * ⚠️ `reativarMonitoramento` (do CLIENTE) e `reativar` (de um EQUIPAMENTO
     * silenciado) são coisas diferentes, e o nome tem de dizer isso. Chamar as
     * duas de "reativar" é como se clica no botão errado.
     */
    reativarMonitoramento: useMutation({
      mutationFn: () => api.post(`${raiz}/reativar`),
      onSuccess: atualizar,
    }),
    retomar: useMutation({
      mutationFn: () => api.post(`${raiz}/retomar`),
      onSuccess: atualizar,
    }),
    salvarOndeRoda: useMutation({
      mutationFn: (v: { ondeRoda: string | null; ondeRodaDetalhe: string | null }) =>
        api.patch(`${raiz}/onde-roda`, v),
      onSuccess: atualizar,
    }),
    pedirVarredura: useMutation({
      mutationFn: () => api.post<{ pendente: boolean; mensagem: string }>(`${raiz}/varredura`),
      onSuccess: atualizar,
    }),
    criarEquipamento: useMutation({
      mutationFn: (e: EquipamentoInput) => api.post<{ id: string }>(`${raiz}/equipamentos`, e),
      onSuccess: atualizar,
    }),
    editarEquipamento: useMutation({
      mutationFn: ({ id, ...e }: EquipamentoInput & { id: string }) =>
        api.patch(`${RAIZ}/equipamentos/${encodeURIComponent(id)}`, e),
      onSuccess: atualizar,
    }),
    silenciar: useMutation({
      mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
        api.post(`${RAIZ}/equipamentos/${encodeURIComponent(id)}/silenciar`, { motivo }),
      onSuccess: atualizar,
    }),
    reativar: useMutation({
      mutationFn: (id: string) => api.post(`${RAIZ}/equipamentos/${encodeURIComponent(id)}/reativar`),
      onSuccess: atualizar,
    }),
    /**
     * 🔴 "Remover" = INATIVAR (mig 565): sai da lista e do teste; o histórico
     * fica. Só admin — e o servidor confere, não só o botão. A resposta diz
     * se há senha no cofre, para a tela perguntar o que fazer com ela.
     */
    inativarEquipamento: useMutation({
      mutationFn: ({ id, motivo }: { id: string; motivo: string }) =>
        api.post<{ ok: boolean; senhaNoCofre: boolean; senhaId: string | null }>(
          `${RAIZ}/equipamentos/${encodeURIComponent(id)}/inativar`,
          { motivo },
        ),
      onSuccess: atualizar,
    }),
  };
}

// ── Formatação — só apresentação, nunca conta ───────────────────────────

/**
 * "há 1h22", "há 11 dias". `null` vira travessão: um estado que nunca mudou
 * não tem "desde".
 */
export function desdeQuando(iso: string | null | undefined, agora = Date.now()): string {
  if (!iso) return '—';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return '—';
  const seg = Math.max(0, Math.round((agora - t) / 1000));
  if (seg < 60) return `há ${seg}s`;
  const min = Math.floor(seg / 60);
  if (min < 60) return `há ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h${String(min % 60).padStart(2, '0')}`;
  const d = Math.floor(h / 24);
  return `há ${d} ${d === 1 ? 'dia' : 'dias'}`;
}

/**
 * "2d 06h", "19h 40m", "45s". Para a coluna "tempo fora".
 *
 * ⚠️ Abaixo de um minuto sai em SEGUNDOS. Arredondar para "0m" faria a tabela
 * dizer que o equipamento não ficou fora, ao lado de uma disponibilidade de
 * 98,2% que só existe porque ele ficou — dois números da mesma linha se
 * contradizendo. Zero de verdade continua sendo travessão.
 */
export function duracao(seg: number): string {
  if (seg <= 0) return '—';
  const d = Math.floor(seg / 86400);
  const h = Math.floor((seg % 86400) / 3600);
  const m = Math.floor((seg % 3600) / 60);
  if (d > 0) return `${d}d ${String(h).padStart(2, '0')}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m`;
  return `${seg}s`;
}

/**
 * 🔴 `null` vira "não medido", NUNCA "0%".
 *
 * Zero diria "ficou fora o mês inteiro" — uma afirmação que ninguém mediu. A
 * diferença entre não saber e saber que foi zero é a diferença entre um painel
 * honesto e um que inventa.
 */
export function porcentagem(v: number | null | undefined, casas = 1): string {
  if (v === null || v === undefined) return 'não medido';
  return `${v.toFixed(casas).replace('.', ',')}%`;
}

export const CORES_ESTADO: Record<EstadoMonitoramento, string> = {
  no_ar: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  fora_do_ar: 'text-red-700 bg-red-50 border-red-200',
  // ⚠️ Cinza de propósito: "sem informação" NÃO é vermelho. Pintá-lo como
  // queda seria dizer com cor o que o texto se recusa a dizer com palavra.
  sem_informacao: 'text-slate-700 bg-slate-100 border-slate-300',
  silenciado: 'text-amber-700 bg-amber-50 border-amber-200',
};
